import { ReactElement } from 'react';
import { ColSizes, ROW_GUTTER } from 'utils/constants/app/ui';
import { createArray } from 'utils/helpers/create-fake-array';
import { ESPORTS_ADDITIONAL_DATA } from 'utils/helpers/moba-games-statistics-options';
import { isMobile } from 'utils/is-mobile';
import { isActiveStreamTabMode } from 'utils/sportsbook/live-games-helper';
import { ADDITIONAL_STATISTICS_LAYOUTS } from 'components/GameDetails/AdditionalStatistics';
import { GameHeaderSkeleton } from 'newcomponents/Shared/sportsbook/GameHeader/GameHeaderSkeleton';
import { Col } from 'newcomponents/UI/Col';
import { Row } from 'newcomponents/UI/Row';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import './index.less';

type TMarketsListSkeleton = {
  banner?: boolean;
  withMatchTracker?: boolean;
  showHeaderSkeleton?: boolean;
  hideNavigationSkeleton?: boolean;
  hideMatchTrackerButton?: boolean;
  isLive?: boolean;
};

const MARKETS_LIST_SKELETON_LENGTH_3 = 3;
const MARKETS_LIST_SKELETON_LENGTH_9 = 9;
const MARKETS_LIST_SKELETON_LENGTH_10 = 10;

export const MarketsListSkeleton = ({
  banner,
  withMatchTracker,
  showHeaderSkeleton,
  hideNavigationSkeleton,
  hideMatchTrackerButton,
  isLive
}: TMarketsListSkeleton): ReactElement => {
  const {
    params: { sport }
  } = useSbRouteMatch();

  const AdditionalLayoutSkeleton =
    ADDITIONAL_STATISTICS_LAYOUTS[sport]?.skeleton;

  const hasAdditionalStatistics =
    Object.keys(ESPORTS_ADDITIONAL_DATA).includes(sport) && isLive;

  return (
    <>
      {banner && (
        <>
          {!hideNavigationSkeleton && (
            <div className="matchHeader__navigation">
              <Skeleton
                active
                title={{ width: '50%', style: { height: 14 } }}
                paragraph={false}
                className="matchHeader__skeleton"
              />
            </div>
          )}
          <div className="matchHeader__wrapper">
            <Skeleton
              active
              round
              title={{ width: '100%', style: { height: 22 } }}
              paragraph={false}
              className="matchHeader__skeleton"
            />
            <Skeleton
              active
              round
              title={{ width: '100%', style: { height: 22 } }}
              paragraph={false}
              className="matchHeader__skeleton"
            />
            <Skeleton
              active
              round
              title={{ width: '100%', style: { height: 22 } }}
              paragraph={false}
              className="matchHeader__skeleton"
            />
          </div>
          {isMobile() && isActiveStreamTabMode() && withMatchTracker
            ? null
            : isMobile() &&
              !hideMatchTrackerButton && (
                <div className="x-matchTracker__buttonWrapper x-matchTracker__buttonWrapper--iframeCollapsed">
                  <Skeleton
                    active
                    title={{
                      style: {
                        height: 30,
                        width: 'calc(100% - 16px)',
                        margin: '0 8px 0 8px'
                      }
                    }}
                    paragraph={false}
                  />
                </div>
              )}

          {hasAdditionalStatistics ? (
            <AdditionalLayoutSkeleton className="additionalStatisticsSkeleton" />
          ) : null}
        </>
      )}
      {showHeaderSkeleton && (
        <>
          <GameHeaderSkeleton />

          <div className="gameDetailsSkeleton">
            <Skeleton
              active
              round
              title={{ width: '30%', style: { height: 22, margin: 0 } }}
              paragraph={false}
            />
            <Skeleton
              className="gameDetailsSkeleton__center"
              active
              round
              title={{
                width: '30%',
                style: { height: 22, margin: 0, justifyContent: 'center' }
              }}
              paragraph={false}
            />
            <Skeleton
              className="gameDetailsSkeleton__end"
              active
              round
              title={{ width: '5%', style: { height: 22, margin: 0 } }}
              paragraph={false}
            />
          </div>
        </>
      )}

      <div className="marketList__marketGroups__container">
        {createArray(MARKETS_LIST_SKELETON_LENGTH_10).map(index => (
          <Skeleton
            key={index}
            active
            title={{
              style: {
                height: 32,
                width: 50,
                borderRadius: 8,
                margin: '0 2px 0 2px'
              }
            }}
            paragraph={false}
          />
        ))}
      </div>
      {createArray(MARKETS_LIST_SKELETON_LENGTH_3).map(index => (
        <div
          key={index}
          style={{
            background: 'var(--v3-black-0)',
            overflow: 'hidden',
            borderRadius: '6px',
            marginBottom: '4px'
          }}
        >
          <Row
            wrap={false}
            style={{
              padding: 12,
              borderTop: '1px solid var(--v3-black-6)',
              borderBottom: '1px solid var(--v3-black-6)'
            }}
          >
            <Col flex="auto">
              <Skeleton
                title={{
                  style: {
                    marginTop: 0,
                    marginBottom: 0,
                    width: 150
                  }
                }}
                paragraph={false}
                active
              />
            </Col>
            <Col flex="none">
              <Skeleton
                title={{
                  style: {
                    marginTop: 0,
                    marginBottom: 0,
                    width: 18
                  }
                }}
                paragraph={false}
                active
              />
            </Col>
          </Row>
          <Row
            gutterV={ROW_GUTTER._4}
            gutterH={ROW_GUTTER._4}
            style={{ padding: '4px 12px 12px 12px' }}
          >
            {createArray(MARKETS_LIST_SKELETON_LENGTH_9).map(key => (
              <Col xs={ColSizes.SIZE_8} key={key}>
                <Skeleton
                  title={{
                    style: {
                      marginTop: 8,
                      marginBottom: 0,
                      height: 32,
                      width: '100%'
                    }
                  }}
                  paragraph={false}
                  active
                />
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </>
  );
};
